/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import {
  ChangeDetectorRef,
  Component, HostListener, Input, OnInit,
} from '@angular/core';
import { UndoRedoService } from 'src/ckeditor/editor/services/undo-redo.service';
import { BulbCKEStateService } from 'src/ckeditor/editor/services/bulb-cke-state.service';
import { CKEditorService } from '../../editor/services/ckeditor.service';

@Component({
  selector: 'bulb-cke-image',
  templateUrl: './cke-image.component.html',
  styleUrls: ['./cke-image.component.scss'],
  standalone: false,
})

/**
 * Component to display Cke images.
 */
export class CkeImageComponent implements OnInit {
  /**
   * Id of the image which will be used to fetch image.
   */
  @Input() imageId: string;

  /**
   * The asset of the image to display.
   */
  @Input() imageAsset: any;

  /**
 * Whether or not the current page the image is on is being edited
 */
  @Input() isEditingPage?: boolean;

  /**
   * show placeholder until cke image get loaded.
   */
  public showPlaceholder: boolean = false;

  /**
   * Contains the caption element of image which is loading.
   */
  private captionElement: any;

  /**
   * Constructor.
   *
   * @param UndoRedoService
   * @param BulbCKEStateService
   * @param cdr
   */
  constructor(
    private undoRedoService : UndoRedoService,
    private bulbCKEStateService : BulbCKEStateService,
    private ckEditorService: CKEditorService,
    private cdr: ChangeDetectorRef,
  ) { }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    document.body.style.overflow = 'auto';
    this.captionHandler(this.imageId);
    this.showPlaceholder = true;
    // Listen if content block has been updated.
    this.undoRedoService.contentBlockSubject.subscribe((contentBlock: any) => {
      this.updateImageAsset(contentBlock);
    });
  }

  /**
   * Update Image Asset.
   * @param contentBlock Block of image.
   */
  updateImageAsset(contentBlock: any) {
    if (contentBlock.id === this.imageId) {
      this.imageAsset = contentBlock.scaledImageAsset || contentBlock.fullImageAsset;
      this.cdr.detectChanges();
    }
  }

  /**
   * Listen to the event that image cropped or rotated.
   */
  @HostListener('window:imageCropComplete', ['$event'])
  @HostListener('window:imageRotationComplete', ['$event'])
  onImageCropComplete(event) {
    this.updateImageAsset(event.detail.contentBlock);
  }

  /**
   * Hide cke image caption until image loaded completely.
   *
   * @param imageId Id of image on which caption has to be hidden.
   *
   */
  captionHandler(imageId: string) {
    const figureElement:any = document.querySelector(`[bulb-block-id='${imageId}']`);
    if (figureElement) {
      this.captionElement = figureElement.querySelector('figcaption');
      this.captionElement?.classList.add('ck-hidden');
    }
  }

  /**
   * Update the cke resize handler position.
   *
   * @param event Contains the information about old and new rectangle dimensions.
   * @param imageid Id of image on which operation is performed.
   * @param imageCreditId Id of image on which credit has to be displayed.
   * @param imageAsset Contains data of image.
   */
  // eslint-disable-next-line class-methods-use-this
  resizeHandler(event:any, imageId: string, imageCreditId: string, imageAsset: any) {
    const figureElement:any = document.querySelector(`[bulb-block-id='${imageId}']`);
    if (figureElement) {
      setTimeout(this.ckEditorService.addMarginToListForCKEditor);
      if (figureElement.classList.contains('image-carousel')) {
        document.getElementById(imageCreditId).style.height = `${imageAsset.height}px`;
      }
      const resizerElement = figureElement.getElementsByClassName('ck-widget__resizer');
      if (resizerElement && resizerElement.length > 0) {
        const { style } = resizerElement[0];
        style.height = `${event.newRect.height}px`;
        style.width = `${event.newRect.width}px`;
      }
    }
  }

  /**
   * Show cke image caption once image loaded completely.
   */
  showCaption() {
    this.showPlaceholder = false;
    this.captionElement?.classList.remove('ck-hidden');
    this.bulbCKEStateService.handleSaveOperation.next(true);
  }
}
